<template>
	<row-object-page name="customer_edit" :oref="oref"  v-model="graph"
		 :build-badge-url="buildBadgeEndpoint"
		 :parent-errors="errors"
		 :force-info="true"
		 ref="rowObject">
		<template #hero><duplicate-parties :data="graph" :disabled="false"></duplicate-parties></template>
	</row-object-page>
</template>

<script setup lang="ts">
import {ref, computed, reactive, onBeforeUnmount, onUnmounted} from "vue";
import RowObjectPage from "@/pages/row-object/RowObjectPage.vue";
import {hasIdentity, objectRef, RowObjectInit, RowObjectRef, RowObjectType, SourceType, SrcType} from "@t/meta/SourceType.ts";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {NEW_RECS_GROUP_ID, PRINTS_GROUP_ID, SideMenu, TOOLS_GROUP_ID, VIEWS_GROUP_ID} from "@t/ui/Menu.ts";
import {connectEventBus, injectEnv} from "@fd/Environment.ts";
import {PartyGraphWithMainTrinity} from "@t/customer/Customer.ts";
import {hasAccess} from "@t/security/ObjectAccess.ts";
import {PartyType} from "@t/customer/PartyType.ts";
import {oneOf} from "@t/fd/Lib.ts";
import {ErrorMap} from "@fd/PartInjectKeys.ts";
import DuplicateParties from "@fd/helpers/DuplicateParties.vue";
import {format, parseDate} from "@t/util/DateUtil.ts";
import {useVirtualRoute} from "@/router/virtual/VirtualRouter.ts";
import {CREATE, EDIT, REPLACE_INIT_WITH_EDIT} from "@/pages/row-object/RowObjectEvents.ts";
import {standardNewRecordGroup} from "@/store/MenuBuilder.ts";

const props = withDefaults(defineProps<{
	unscoped?: boolean
}>(), {
	unscoped: false
});
const ns = useNavigationStore();
const env = injectEnv();
const bus = connectEventBus(props.unscoped ? null : "PartyPage");//.debugging();

const graph = ref<PartyGraphWithMainTrinity>(null);
const rowObject = ref<InstanceType<typeof RowObjectPage> | null>(null);
const route = useVirtualRoute();
const oref = objectRef(route, SrcType.PARTY);
const id = computed<number|string>(() => hasIdentity(oref.value) ? oref.value.id : 0);
const errors = reactive<ErrorMap>([]);

function countMembersOfType(...types:PartyType[]):number {
	if (graph.value?.members?.length) {
		let count = 0;
		for (let item of graph.value.members) {
			if (oneOf(item.party.type, ...types)) count++;
		}
		return count;
	}
	return 0;
}

const peopleCount = computed<number>(() => {
	return countMembersOfType("INDIVIDUAL", "FREELANCER");
});

const branchCount = computed<number>(() => {
	return countMembersOfType("BRANCH");
});

ns.listenForStackerUpdates(bus, oref, () => env.msg('title.createCustomer'),
	"/editParty.do", () => graph.value.party.fileAs, () => graph.value.party.id);

function doEdit(oref:RowObjectRef) {
	if (oref.type === SrcType.PARTY) {
		delayedTimeout = setTimeout(doDelayedActions, 200);
		ns.chatContext = { url: "editParty.do?id="+graph.value.party.id, title: graph.value.party.fileAs, icon: graph.value.party.icon };
	}
}
bus.on(EDIT, doEdit);

ns.defineMenu((mb) => {
	if (id.value && rowObject.value && rowObject.value.okLoaded()) {
		mb.pageNamed("customer_edit");
		mb.inGroup(NEW_RECS_GROUP_ID, standardNewRecordGroup({ mailMembers: true, facts: true, party: false }));
		if (graph.value && hasAccess(graph.value.party, "sensitive")) {
			mb.inGroup(VIEWS_GROUP_ID, (gb) => {
				if (Server.mod.email) gb.navItem("filterCustomerEmails", "filterCustomersObjects.do?obj=emails&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsEmails", "filter");
				if (Server.mod.sms) gb.navItem("filterCustomerSMS", "filterCustomersObjects.do?obj=sms&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsSMS", null);
				if (Server.mod.opportunity) gb.navItem("filterCustomerOpportunities", "filterCustomersObjects.do?obj=opportunities&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsOpportunities", null);
				if (Server.mod.offer) gb.navItem("filterCustomerOffers", "filterCustomersObjects.do?obj=offers&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsOffers", null);
				if (Server.mod.communication) gb.navItem("filterCustomerContacts", "filterCustomersObjects.do?obj=contacts&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsContacts", null);
				gb.navItem("filterCustomerEvents", "filterCustomersObjects.do?obj=events&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsEvents", null);
				if (Server.mod.buscase) gb.navItem("filterCustomerBuscases", "filterCustomersObjects.do?obj=buscases&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsBusCases", null);
				if (Server.mod.task) gb.navItem("filterCustomerTasks", "filterCustomersObjects.do?obj=tasks&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsTasks", null);
				if (Server.mod.product) gb.navItem("filterCustomerProducts", "filterCustomersObjects.do?obj=productcustomers&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsProducts", null);
				if (Server.mod.invoice) gb.navItem("filterCustomerInvoices", "filterCustomersObjects.do?obj=invoices&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsInvoices", null);
				if (Server.mod.buscase) gb.navItem("filterCustomerBuscaseItems", "filterBusCaseItems.do?_resetStack=no&id=" + id.value, "menu.filterBcItems", null);
				if (Server.mod.helpdesk) gb.navItem("filterCustomerProblems", "filterCustomersObjects.do?obj=problems&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsProblems", null);
				if (Server.mod.worksheet) gb.navItem("filterCustomerWorksheets", "filterCustomersObjects.do?obj=worksheets&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsWorksheets", null);
				if (peopleCount.value > 0) gb.navItem("filterCustomerPeople", "filterCustomersObjects.do?obj=people&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsPeople", null);
				if (branchCount.value > 0) gb.navItem("filterCustomerBranches", "filterCustomersObjects.do?obj=branches&_resetStack=no&customer=" + id.value, "menu.filterCustomersObjectsBranches", null);

			});
		}
		mb.inGroup(TOOLS_GROUP_ID, (gb) => {
			gb.navItem("seeThis", "see.do?id="+id.value+"&type="+SrcType.PARTY, "menu.seeThis", "eye-eyebrow");
			gb.navItem("recordFiles", "showRecordFiles.do?srcId="+id.value+"&srcType="+SrcType.PARTY, "menu.recordFiles", "volume");
			gb.navItem("duplicateCustomer", "createParty.do?copy="+id.value, "menu.duplicateCustomer", "copy");
			if (graph.value && hasAccess(graph.value.party, "delete")) {
				gb.confirmDelete(gb.navItem("deleteCustomer", "deleteCustomer.do?id="+id.value, "title.deleteCustomer", "trash"), "dial.deleteCustomer");
			}
		});
		if (graph.value && hasAccess(graph.value.party, "sensitive")) {
			mb.inGroup(PRINTS_GROUP_ID, (gb) => {
				gb.navItem("printCustomer1", "outputParams.do?descriptor="+SrcType.PARTY+"&section="+ns.activeSection+"&pk="+id.value, "menu.printCustomer", "printer");
				if (Server.mod.product) {
					let item = gb.navItem("printCustomerProducts", "printCustomerProducts.do?id="+id.value, "menu.printCustomerProducts", "printer");
					item.target = "_blank";
				}
				gb.navItem("profitMarginRatioSingle", "profitMarginRatioSingle.do?type="+SrcType.PARTY+"&id="+id.value, "menu.profitMarginRatio", "chart-pie");
			});
		}
	}
});


function buildBadgeEndpoint(endpoint:string):string {
	const t = graph.value.trinity;
	if (t) {
		if (t.customer) endpoint = env.net.urlParam(endpoint, "subCustomer", t.customer);
		if (t.branch) endpoint = env.net.urlParam(endpoint, "subBranch", t.branch);
		if (t.individual) endpoint = env.net.urlParam(endpoint, "subPerson", t.individual);
	}
	return endpoint;
}

function insertExtraErrors(errs: ErrorMap) {
	errors.length = 0;
	errors.push(...errs);
	if (graph.value?.party?.isirStatus) {
		let isirDate = "-";
		if (graph.value?.party?.isirLastCheck) {
			const pd = parseDate(graph.value.party.isirLastCheck);
			if (pd.date) {
				isirDate = format(pd, env);
			}
		}
		errors.push({
			level: "WARNING",
			message: env.msg('isir.warning', graph.value?.party?.isirUrl ?? "", graph.value.party.isirStatus, isirDate),
			icon: "binoculars",
			html:true
		});
	}
}

let delayedTimeout = 0;

function doDelayedActions() {
	delayedTimeout = 0;
	env.net.fetch("api/parties/"+id.value+"/warnings")
		.then((resp) => {
			if (resp.ok) {
				resp.json().then((e:ErrorMap) => {
					insertExtraErrors(e);
				})
			}
		});
}

onBeforeUnmount(() => {
	if (delayedTimeout) {
		clearTimeout(delayedTimeout);
		delayedTimeout = 0;
	}
});

</script>

<style>

</style>